import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';

import { User, Org, Role } from '../../models/user';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { BroadcastChannel } from 'broadcast-channel';
import { ConfigService } from '../../services/config.service';
import { HttpParams } from '@angular/common/http';
import { AwaitLock } from '../../services/awaitLock';
import { distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import equal from 'fast-deep-equal/es6';
import { SettingsService } from 'src/app/services/settings.service';
import { OrgSiteSelectionService } from '@app/shared/org-site-selection/org-site-selection.service';
import { RoleEnum } from '@app/api-module/user-management-api-client-v2';
import { PendoService } from '@app/services/pendo/pendo-service';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const pendo: any;

export type PermissionErrorType = 'no_org';
export class PermissionError extends Error {
    readonly type: PermissionErrorType;

    constructor(m: string, type: PermissionErrorType) {
        super(m);

        this.type = type;

        // Set the prototype explicitly.
        Object.setPrototypeOf(this, PermissionError.prototype);
    }
}

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService implements OnDestroy {
    constructor(
        private readonly oAuthService: OAuthService,
        private readonly configService: ConfigService,
        private readonly settingsService: SettingsService,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly pendoService: PendoService
    ) {
        this.currentUserSubject = new BehaviorSubject<User>(null);
        this.currentUser = this.currentUserSubject.asObservable();
        this.configureBroadcastChannel();
        this.subscriptions.add(
            this.oAuthService.events.subscribe(event => {
                switch (event.type) {
                    case 'token_received':
                        // Refresh tokens in currentUser
                        this.loginWithTokens();
                        break;
                }
            })
        );

        this.currentUserCountryCode$ = this.currentUser
            .pipe(
                filter(user => !!user),
                distinctUntilChanged((prev, curr) =>
                    equal(prev.orgs.values().next().value.orgCode, curr.orgs.values().next().value.orgCode)
                ),
                switchMap((user: User) =>
                    this.settingsService.getClientByOrg(user.orgs.values().next().value.orgCode).pipe(map(client => client.countryCode))
                )
            )
            .pipe(shareReplay());
    }

    static readonly FEATURE_ORDER_FORM = 'orderForm';

    static readonly PERMISSION_CREATE_ORDER = 'createOrder';

    static readonly PERMISSION_VIEW_ORDER = 'viewOrder';

    private readonly namespace = 'https://www.everlightradiology.com/cc/';
    private readonly keyAppMetaData = this.namespace + 'app_metadata';
    private readonly keyUserMetaData = this.namespace + 'user_metadata';
    private readonly keyPasswordChangeRequired = this.namespace + 'password_change_required';

    private readonly currentUserSubject: BehaviorSubject<User>;
    public currentUser: Observable<User>;
    private readonly currentUserCountryCode$: Observable<string>;

    private logoutBroadcastChannel: BroadcastChannel;

    private tokenUpdatedBroadcastChannel: BroadcastChannel;

    private readonly subscriptions = new Subscription();

    private readonly tokenRefreshLock = new AwaitLock();

    private static areOrgsEqual(leftOrgs: Map<string, Org>, rightOrgs: Map<string, Org>): boolean {
        // Map type of `orgs` and Map type of `features` cannot be JSON.stringify()-ed.
        // So, actually compare them by value (shock/horror).
        if (leftOrgs.size !== rightOrgs.size) {
            return false;
        }

        for (const entry of Array.from(leftOrgs.entries())) {
            const orgCode = entry[0];
            const leftOrg = entry[1];

            const rightOrg = rightOrgs.get(orgCode);
            if (!rightOrg) {
                return false;
            }

            // Do not check client name & country code, as it is not part of the initial token.
            const orgDetailsMatch =
                leftOrg.defaultSiteId === rightOrg.defaultSiteId &&
                leftOrg.enabled === rightOrg.enabled &&
                leftOrg.roles.length === rightOrg.roles.length;
            if (!orgDetailsMatch) {
                return false;
            }

            for (const leftRole of leftOrg.roles) {
                const rightRole = rightOrg.roles.find(role => role.id === leftRole.id);
                const rolesEqual = this.areRolesEqual(leftRole, rightRole);
                if (!rolesEqual) {
                    return false;
                }
            }
        }

        return true;
    }

    private static areRolesEqual(leftRole: Role, rightRole: Role): boolean {
        return (
            rightRole &&
            leftRole.name === rightRole.name &&
            leftRole.sites.length === rightRole.sites.length &&
            leftRole.sites.every((siteId, index) => rightRole.sites[index] === siteId)
        );
    }

    private configureBroadcastChannel() {
        const that = this;

        this.logoutBroadcastChannel = new BroadcastChannel('logout');
        this.logoutBroadcastChannel.addEventListener('message', _ => {
            const currentUser = this.getCurrentUser();
            if (currentUser) {
                that.doLogout();
            }
        });

        this.tokenUpdatedBroadcastChannel = new BroadcastChannel('tokenUpdated');
        this.tokenUpdatedBroadcastChannel.addEventListener('message', async _ => {
            console.log('Refreshing authentication token.');
            await this.refreshToken();
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.logoutBroadcastChannel.close();
        this.tokenUpdatedBroadcastChannel.close();
    }

    getCurrentUser(): User {
        return this.currentUserSubject.value;
    }

    private login(idToken: string): boolean {
        if (!idToken) {
            return false;
        }

        this.refreshAccessToken();

        const user: User = this.getUserFromTokens(idToken);

        // the number of orgs must be 1 or greater
        if (!user.orgs || user.orgs.size === 0) {
            throw new PermissionError('the number of orgs must be 1', 'no_org');
        } else {
            if (
                user &&
                this.currentUserSubject.value &&
                JSON.stringify(user) === JSON.stringify(this.currentUserSubject.value) &&
                AuthenticationService.areOrgsEqual(this.currentUserSubject.value.orgs, user.orgs)
            ) {
                // If no change to user, don't fire next() to cause downstream components reloading the user.
                return true;
            }

            this.currentUserSubject.next(user);
            this.initPendo(user);
            this.pendoService.trackEvent({
                Type: 'ScreenInitialSize',
                Body: {
                    AppName: 'ControlCentreApp',
                    ScreenWidth: JSON.stringify(screen.width),
                    ScreenHeight: JSON.stringify(screen.height),
                    WindowWidth: JSON.stringify(window.innerWidth),
                    WindowHeight: JSON.stringify(window.innerHeight)
                }
            });
            return true;
        }
    }

    private getUserFromTokens(idToken: string): User {
        let user: User = null;

        if (!idToken) {
            return user;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let parsedIdToken: any;

        try {
            const payload = idToken.split('.')[1];
            const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
            parsedIdToken = JSON.parse(decodedPayload);
        } catch (e) {
            console.log('Failed to parse ID token: ' + e);
            return user;
        }

        user = new User({
            id: parsedIdToken.sub || undefined,
            username: parsedIdToken.name || undefined,
            email: parsedIdToken.email || undefined,
            title: parsedIdToken[this.keyUserMetaData].title || undefined,
            firstName: parsedIdToken.given_name || undefined,
            surname: parsedIdToken.family_name || undefined,
            passwordChangeRequired: parsedIdToken[this.keyPasswordChangeRequired] || false
        });

        if (parsedIdToken[this.keyUserMetaData].phoneNumbers) {
            const defaultPhoneNumber = parsedIdToken[this.keyUserMetaData].phoneNumbers.find(p => p.isDefault === true);
            if (defaultPhoneNumber) {
                user.countryCode = defaultPhoneNumber.countryCode;
                user.number = defaultPhoneNumber.number;
                user.extension = defaultPhoneNumber.extension;
            }
        }

        const appMetadata = parsedIdToken[this.keyAppMetaData];
        if (appMetadata?.orgs) {
            user.orgs = new Map<string, Org>();

            const appMetadataOrgs = appMetadata.orgs;
            Object.keys(appMetadataOrgs).forEach(keyOrg => {
                const org = new Org({ orgCode: keyOrg, clientName: null, countryCode: null });
                user.orgs.set(keyOrg, org);

                const appMetadataOrg = appMetadataOrgs[keyOrg];

                if (appMetadataOrg) {
                    org.enabled = appMetadataOrg.enabled;
                    org.defaultSiteId = appMetadataOrg.defaultSiteId;

                    if (appMetadataOrg.roles) {
                        org.roles = [...appMetadataOrg.roles];
                    }
                }
            });
        }

        return user;
    }

    doLogout(directedToLogoutURL = false): void {
        this.logoutBroadcastChannel.postMessage({});
        this.currentUserSubject.next(null);
        this.oAuthService.logOut(directedToLogoutURL);
    }

    async loginWithTokens(): Promise<boolean> {
        if (this.oAuthService.hasValidIdToken() && this.oAuthService.hasValidAccessToken()) {
            const idToken = this.oAuthService.getIdToken();
            return this.login(idToken);
        } else {
            try {
                return await this.refreshToken();
            } catch (e) {
                // Token refresh failure. There is no way to recover.
                return false;
            }
        }
    }

    async notifyUserUpdated(): Promise<void> {
        // Refresh this app
        await this.refreshToken().then(() => {
            // Notify other app to refresh
            this.tokenUpdatedBroadcastChannel.postMessage({});
        });
    }

    private async refreshToken() {
        await this.tokenRefreshLock.acquireAsync();
        try {
            if (this.oAuthService.getRefreshToken()) {
                await this.oAuthService.refreshToken();
            } else {
                throw new Error('No refresh token to refresh id token with.');
            }
        } finally {
            this.tokenRefreshLock.release();
        }

        const idToken = this.oAuthService.getIdToken();
        return this.login(idToken);
    }

    configureOAuthServiceLogoutUrlWithMessage(message: string): void {
        // add 'message' parameter into logout returnTo after oAuthService logout
        const returnTo = 'returnTo';
        const authConfig = new AuthConfig(ConfigService.settings.auth);
        const logoutUrl = authConfig.logoutUrl;
        if (logoutUrl.includes('?')) {
            const httpParams = new HttpParams({ fromString: logoutUrl.split('?')[1] });
            const returntoParam = httpParams.get(returnTo);
            if (returntoParam) {
                authConfig.logoutUrl = authConfig.logoutUrl.replace(
                    returnTo + '=' + returntoParam,
                    returnTo + '=' + returntoParam + '%3Fmessage%3D' + message
                );
                this.oAuthService.configure(authConfig);
            }
        }
    }

    private initPendo(user: User) {
        pendo.initialize({
            visitor: {
                id: user.id,
                email: user.email
            }
        });

        const countrySettings$ = this.settingsService.getCountrySettings();
        const currentOrg$ = this.orgSiteSelectionService.getOrgSelected$();
        this.subscriptions.add(
            combineLatest([countrySettings$, currentOrg$]).subscribe(([countrySettings, currentOrg]) => {
                if (!countrySettings || !currentOrg) {
                    return;
                }

                const orgCode = currentOrg.orgCode;
                const countryCode = currentOrg.countryCode;
                const region = countrySettings.find(setting => setting.country === countryCode)?.region;
                const roles = currentOrg.roles.map(role => role.name).join(', ');

                const pendoUser = {
                    visitor: {
                        id: user.id,
                        email: user.email,
                        Org: orgCode,
                        Country: countryCode,
                        Region: region,
                        IsAdmin: user.hasRole(RoleEnum.Admin, orgCode),
                        Roles: roles
                    },
                    account: {
                        id: this.getAccountId(orgCode, region),
                        Region: region,
                        Country: countryCode
                    },
                    parentAccount: {
                        id: region
                    }
                };

                try {
                    pendo.identify(pendoUser);
                    // Need to send account id seperately, because Pendo is bad.
                    pendo.identify(pendoUser.visitor.id, this.getAccountId(orgCode, region));
                } catch (exception) {
                    console.error('Error identifying user to Pendo!');
                    console.error(exception);
                }
            })
        );
    }

    private getAccountId(orgCode: string, region: string): string {
        return region + '::' + orgCode;
    }

    private refreshAccessToken() {
        const hostName = new URL(this.configService.getSettings().apiBaseUrl).hostname;
        const domainSegments = hostName.split('.');
        const segmentCount = domainSegments.length;
        const rootDomain = segmentCount > 2 ? `${domainSegments[segmentCount - 2]}.${domainSegments[segmentCount - 1]}` : hostName;
        const accessToken = this.oAuthService.getAccessToken();
        globalThis.document.cookie = `access_token=${accessToken}; domain=.${rootDomain}; path=/api/hubs/`;
    }
}
